// =============================================================================
// VandieZ admin-web -- TR-04 document compliance + p76 override
// =============================================================================
// The four statutory expiry dates existed on `vehicles` for months with
// nothing reading them. Migration 0070 made them block assignment; this
// panel is where an admin sees that and, when there is a real reason,
// overrides it.
//
// THE DISPLAY STATE HERE IS PRESENTATION ONLY. The server is authoritative
// (services/vehicles/compliance.py) and will refuse an assignment regardless
// of what this renders. The rules are mirrored rather than fetched because
// the compliance endpoint is owner-scoped and an admin is not the owner --
// duplicating three date comparisons is a smaller price than a second
// endpoint that could disagree with the first.
//
// Two behaviours are deliberately copied from the server and worth keeping
// in step if it ever changes:
//   * PUC never blocks. It is an emissions certificate, not insurance.
//   * A missing date is NOT an expired one (D-16). Most vehicles carry
//     NULLs because VehicleCreate never captured expiries.
// =============================================================================

import { AlertTriangle, ShieldCheck, ShieldOff } from "lucide-react";
import type React from "react";
import { useState } from "react";

import { vehicleComplianceAdminApi } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/types/auth";

/** Documents that stop a vehicle working. PUC is absent on purpose. */
const BLOCKING: { key: string; label: string }[] = [
  { key: "insurance_expiry", label: "Insurance" },
  { key: "permit_expiry", label: "Permit" },
  { key: "fitness_expiry", label: "Fitness" },
];

const ADVISORY: { key: string; label: string }[] = [
  { key: "puc_expiry", label: "PUC" },
];

const WARNING_DAYS = 30;

type State = "valid" | "expiring" | "expired" | "missing";

function classify(value: string | null | undefined): {
  state: State;
  days: number | null;
} {
  if (value === null || value === undefined) {
    return { state: "missing", days: null };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(value);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { state: "expired", days };
  if (days <= WARNING_DAYS) return { state: "expiring", days };
  return { state: "valid", days };
}

const TONE: Record<State, string> = {
  valid: "bg-success-subtle text-success-subtle-foreground",
  expiring: "bg-warning-subtle text-warning-subtle-foreground",
  expired: "bg-danger-subtle text-danger-subtle-foreground",
  missing: "bg-muted text-muted-foreground",
};

export interface VehicleExpiryFields {
  insurance_expiry?: string | null;
  permit_expiry?: string | null;
  fitness_expiry?: string | null;
  puc_expiry?: string | null;
  document_block_override_until?: string | null;
  document_block_override_reason?: string | null;
}

interface Props {
  vehicleId: string;
  accessToken: string;
  vehicle: VehicleExpiryFields;
  onChanged: () => void;
}

export function VehicleCompliancePanel({
  vehicleId,
  accessToken,
  vehicle,
  onChanged,
}: Props): React.ReactElement {
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = [...BLOCKING, ...ADVISORY].map((d) => ({
    ...d,
    blocking: BLOCKING.some((b) => b.key === d.key),
    value: vehicle[d.key as keyof VehicleExpiryFields] as string | null,
    ...classify(vehicle[d.key as keyof VehicleExpiryFields] as string | null),
  }));

  const expiredBlockers = rows.filter((r) => r.blocking && r.state === "expired");
  const overrideUntil = vehicle.document_block_override_until ?? null;
  const overrideActive =
    overrideUntil !== null && new Date(overrideUntil) >= new Date();
  const isBlocked = expiredBlockers.length > 0 && !overrideActive;

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setShowForm(false);
      setReason("");
      setUntil("");
      onChanged();
    } catch (err) {
      // NOTHING_TO_OVERRIDE is the interesting refusal: the server rejects a
      // pre-emptive override because one would lie dormant and then
      // authorise a future lapse nobody reviewed.
      setError(
        err instanceof ApiError ? err.message : "That did not work.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-medium">
          {isBlocked ? (
            <ShieldOff className="h-4 w-4 text-destructive" />
          ) : (
            <ShieldCheck className="h-4 w-4" />
          )}
          Document compliance
        </h3>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            isBlocked ? TONE.expired : TONE.valid
          }`}
        >
          {isBlocked ? "Blocked from assignment" : "Can be assigned"}
        </span>
      </div>

      <dl className="space-y-1 text-sm">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">
              {r.label}
              {!r.blocking ? (
                <span className="ml-1 text-xs">(advisory)</span>
              ) : null}
            </dt>
            <dd className="flex items-center gap-2">
              <span>{r.value ?? "not recorded"}</span>
              <span className={`rounded px-1.5 py-0.5 text-xs ${TONE[r.state]}`}>
                {r.state === "missing"
                  ? "no date"
                  : r.state === "expired"
                    ? `${Math.abs(r.days ?? 0)}d overdue`
                    : r.state === "expiring"
                      ? `${r.days}d left`
                      : "valid"}
              </span>
            </dd>
          </div>
        ))}
      </dl>

      {/*
        A missing date is a data-entry gap, not a compliance failure, and it
        is the common case -- vehicle creation never captured expiries. Say
        so explicitly so nobody reads "no date" as "lapsed".
      */}
      {rows.some((r) => r.state === "missing") ? (
        <p className="text-xs text-muted-foreground">
          Missing dates do not block assignment — they were never captured at
          registration. Chase the owner rather than overriding.
        </p>
      ) : null}

      {overrideActive ? (
        <div className="space-y-2 rounded-md bg-warning-subtle p-3 text-sm text-warning-subtle-foreground">
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" />
            Running on an override until {overrideUntil}
          </p>
          {vehicle.document_block_override_reason !== null &&
          vehicle.document_block_override_reason !== undefined ? (
            <p>{vehicle.document_block_override_reason}</p>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void act(() =>
                vehicleComplianceAdminApi.revoke(accessToken, vehicleId),
              )
            }
          >
            Revoke override
          </Button>
        </div>
      ) : null}

      {error !== null ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : null}

      {isBlocked && !showForm ? (
        <Button size="sm" variant="outline" onClick={() => { setShowForm(true); }}>
          Record an override
        </Button>
      ) : null}

      {showForm ? (
        <div className="space-y-3 rounded-md border p-3">
          <p className="text-xs text-muted-foreground">
            The reason and end date are both recorded against your account.
            An override without them is indistinguishable from a bug, and one
            without an end date is how a temporary exception becomes
            permanent.
          </p>
          <div className="space-y-1">
            <Label htmlFor="override-reason">Reason (min 10 characters)</Label>
            <Textarea
              id="override-reason"
              rows={2}
              value={reason}
              placeholder="Renewal in progress, policy #123456, receipt sighted"
              onChange={(e) => { setReason(e.target.value); }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="override-until">Valid until</Label>
            <Input
              id="override-until"
              type="date"
              value={until}
              onChange={(e) => { setUntil(e.target.value); }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || reason.trim().length < 10 || until === ""}
              onClick={() =>
                void act(() =>
                  vehicleComplianceAdminApi.override(accessToken, vehicleId, {
                    reason: reason.trim(),
                    until,
                  }),
                )
              }
            >
              Save override
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowForm(false); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VehicleCompliancePanel;
